import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { permissionGuard } from './guards/permission.guard';
import { statewideGuard } from './guards/statewide.guard';
import { anyPermissionGuard } from './guards/any-permission.guard';
import { billingContactOrPermissionGuard } from './guards/billing-contact-or-permission.guard';

export const routes: Routes = [
  {
    path: '',
    title: 'Edjoin Admin',
    pathMatch: 'full',
    loadComponent: () => import('./pages/landing/landing.component').then((m) => m.LandingComponent),
  },
  {
    path: 'login',
    title: 'Login',
    loadComponent: () => import('./pages/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'signup',
    title: 'Create Account',
    loadComponent: () => import('./pages/signup/signup.component').then((m) => m.SignupComponent),
  },
  {
    // Public-anonymous recipient page for the AB2534/SB848 Mandatory Employment Disclosure
    // workflow. Reached via the tokened URL emailed to the prior employer's HR contact;
    // GUID in the URL is the only credential. No guards.
    path: 'disclosure/respond/:token',
    title: 'Mandatory Employment Disclosure',
    loadComponent: () => import('./pages/recipient-disclosure/recipient-disclosure-page.component').then((m) => m.RecipientDisclosurePageComponent),
  },
  {
    // MED print view (#44010): the request letter + response, outside the dashboard shell so
    // it prints without the app chrome. Opened in a new tab from the MED Tracking page.
    path: 'employment-disclosures/:recordId/print',
    title: 'Employment Disclosure Request',
    canActivate: [authGuard, permissionGuard('ViewEmploymentDisclosures')],
    loadComponent: () => import('./pages/employment-disclosure-print/employment-disclosure-print.component').then((m) => m.EmploymentDisclosurePrintComponent),
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        title: 'Overview',
        loadComponent: () => import('./pages/dashboard/overview/overview.component').then((m) => m.OverviewComponent),
      },
      {
        path: 'bank',
        title: 'Applicant Bank',
        canActivate: [permissionGuard('ViewApplicantBank')],
        loadComponent: () => import('./pages/dashboard/bank/bank.component').then((m) => m.BankComponent),
      },
      {
        path: 'applicants/:applicantId',
        title: 'Applicant Profile',
        canActivate: [permissionGuard('ViewApplicantBank')],
        loadComponent: () => import('./pages/dashboard/applicant-profile/applicant-profile.component').then((m) => m.ApplicantProfileComponent),
      },
      {
        path: 'job-postings/:id/applicants',
        title: 'Applicants',
        // ViewAssignedApplicants users reach the same route; the API scopes the result
        // to postings they're assigned to as Screener/Reviewer.
        canActivate: [anyPermissionGuard(['ViewApplicants', 'ViewAssignedApplicants'])],
        loadComponent: () => import('./pages/dashboard/applicant-tracking/applicant-tracking.component').then((m) => m.ApplicantTrackingComponent),
      },
      {
        path: 'tracking-summary',
        title: 'Applicant Tracking',
        canActivate: [anyPermissionGuard(['ViewApplicants', 'ViewAssignedApplicants'])],
        loadComponent: () => import('./pages/dashboard/tracking-summary/tracking-summary.component').then((m) => m.TrackingSummaryComponent),
      },
      {
        path: 'eligibility-lists',
        title: 'Eligibility Lists',
        canActivate: [permissionGuard('ViewApplicants')],
        loadComponent: () => import('./pages/dashboard/eligibility-lists/eligibility-lists.component').then((m) => m.EligibilityListsComponent),
      },
      {
        path: 'eligibility-lists/new',
        title: 'New Eligibility List',
        canActivate: [permissionGuard('ManageApplicants')],
        loadComponent: () => import('./pages/dashboard/eligibility-list-edit/eligibility-list-edit.component').then((m) => m.EligibilityListEditComponent),
      },
      {
        path: 'eligibility-lists/:id',
        title: 'Edit Eligibility List',
        canActivate: [permissionGuard('ViewApplicants')],
        loadComponent: () => import('./pages/dashboard/eligibility-list-edit/eligibility-list-edit.component').then((m) => m.EligibilityListEditComponent),
      },
      {
        path: 'job-postings',
        title: 'Job Postings',
        canActivate: [permissionGuard('ViewPostings')],
        loadComponent: () => import('./pages/dashboard/job-postings/job-postings.component').then((m) => m.JobPostingsComponent),
      },
      {
        path: 'job-postings/new',
        title: 'New Job Posting',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/job-posting-edit/job-posting-edit.component').then((m) => m.JobPostingEditComponent),
      },
      {
        path: 'job-postings/:id/edit',
        title: 'Edit Job Posting',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/job-posting-edit/job-posting-edit.component').then((m) => m.JobPostingEditComponent),
      },
      {
        path: 'job-postings/:id/materials-tracking',
        title: 'Materials Tracking',
        canActivate: [anyPermissionGuard(['ViewApplicants', 'ViewAssignedApplicants'])],
        loadComponent: () => import('./pages/dashboard/applicant-materials-tracking/applicant-materials-tracking.component').then((m) => m.ApplicantMaterialsTrackingComponent),
      },
      {
        path: 'employment-disclosure-tracking',
        title: 'Employment Disclosure Forms',
        canActivate: [permissionGuard('ViewEmploymentDisclosures')],
        loadComponent: () => import('./pages/dashboard/employment-disclosure-tracking/employment-disclosure-tracking.component').then((m) => m.EmploymentDisclosureTrackingComponent),
      },
      {
        path: 'job-postings/:postingId/applicants/:applicationId/employment-disclosures',
        title: 'Employment Disclosures',
        canActivate: [permissionGuard('ViewEmploymentDisclosures')],
        loadComponent: () => import('./pages/dashboard/employment-disclosures/employment-disclosures.component').then((m) => m.EmploymentDisclosuresComponent),
      },
      {
        path: 'job-postings/:id/interview-sessions',
        title: 'Interview Sessions',
        canActivate: [anyPermissionGuard(['ViewApplicants', 'ViewAssignedApplicants'])],
        loadComponent: () => import('./pages/dashboard/interview-sessions/interview-sessions.component').then((m) => m.InterviewSessionsComponent),
      },
      {
        path: 'interview-management',
        title: 'Interview Management',
        canActivate: [anyPermissionGuard(['ViewApplicants', 'ViewAssignedApplicants'])],
        loadComponent: () => import('./pages/dashboard/interview-management/interview-management.component').then((m) => m.InterviewManagementComponent),
      },
      {
        path: 'recruitment-events',
        title: 'Recruitment Events',
        canActivate: [permissionGuard('ViewPostings')],
        loadComponent: () => import('./pages/dashboard/recruitment-events/recruitment-events.component').then((m) => m.RecruitmentEventsComponent),
      },
      {
        path: 'recruitment-events/new',
        title: 'New Recruitment Event',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/recruitment-event-edit/recruitment-event-edit.component').then((m) => m.RecruitmentEventEditComponent),
      },
      {
        path: 'recruitment-events/:id',
        title: 'Edit Recruitment Event',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/recruitment-event-edit/recruitment-event-edit.component').then((m) => m.RecruitmentEventEditComponent),
      },
      {
        path: 'application-templates',
        title: 'Application Templates',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/application-templates/application-templates.component').then((m) => m.ApplicationTemplatesComponent),
      },
      {
        path: 'application-templates/:id',
        title: 'Application Template',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/application-template-edit/application-template-edit.component').then((m) => m.ApplicationTemplateEditComponent),
      },
      {
        path: 'file-library',
        title: 'File Library',
        canActivate: [permissionGuard('ViewFiles')],
        loadComponent: () => import('./pages/dashboard/file-library/file-library.component').then((m) => m.FileLibraryComponent),
      },
      {
        path: 'file-library/new',
        title: 'Upload File',
        canActivate: [permissionGuard('ManageFiles')],
        loadComponent: () => import('./pages/dashboard/file-library-upload/file-library-upload.component').then((m) => m.FileLibraryUploadComponent),
      },
      {
        path: 'required-materials',
        title: 'Required Materials',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/required-material-sets/required-material-sets.component').then((m) => m.RequiredMaterialSetsComponent),
      },
      {
        path: 'required-materials/:id',
        title: 'Required Material Set',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/required-material-set-edit/required-material-set-edit.component').then((m) => m.RequiredMaterialSetEditComponent),
      },
      {
        path: 'screening-questions',
        title: 'Screening Questions',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/screening-question-sets/screening-question-sets.component').then((m) => m.ScreeningQuestionSetsComponent),
      },
      {
        path: 'screening-questions/:id',
        title: 'Screening Question Set',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/screening-question-set-edit/screening-question-set-edit.component').then((m) => m.ScreeningQuestionSetEditComponent),
      },
      {
        path: 'rating-sheets',
        title: 'Rating Sheets',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/rating-sheets/rating-sheets.component').then((m) => m.RatingSheetsComponent),
      },
      {
        path: 'rating-sheets/:id',
        title: 'Rating Sheet',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/rating-sheet-edit/rating-sheet-edit.component').then((m) => m.RatingSheetEditComponent),
      },
      {
        path: 'interview-locations',
        title: 'Interview Locations',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/interview-locations/interview-locations.component').then((m) => m.InterviewLocationsComponent),
      },
      {
        path: 'summer-schools',
        title: 'Summer School Schools',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/summer-schools/summer-schools.component').then((m) => m.SummerSchoolsComponent),
      },
      {
        path: 'summer-courses',
        title: 'Summer School Courses',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/summer-courses/summer-courses.component').then((m) => m.SummerCoursesComponent),
      },
      {
        path: 'reference-questions',
        title: 'Reference Questions',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/reference-question-sets/reference-question-sets.component').then((m) => m.ReferenceQuestionSetsComponent),
      },
      {
        path: 'reference-questions/:id',
        title: 'Reference Question Set',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/reference-question-set-edit/reference-question-set-edit.component').then((m) => m.ReferenceQuestionSetEditComponent),
      },
      {
        path: 'hire-packets',
        title: 'Hire Packets',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/hire-packets/hire-packets.component').then((m) => m.HirePacketsComponent),
      },
      {
        path: 'hire-packets/:id',
        title: 'Hire Packet',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/hire-packet-edit/hire-packet-edit.component').then((m) => m.HirePacketEditComponent),
      },
      {
        path: 'salary-schedules',
        title: 'Salary Schedules',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/salary-schedules/salary-schedules.component').then((m) => m.SalarySchedulesComponent),
      },
      {
        path: 'salary-schedules/:id',
        title: 'Salary Schedule',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/salary-schedule-edit/salary-schedule-edit.component').then((m) => m.SalaryScheduleEditComponent),
      },
      {
        path: 'tracking-options',
        title: 'Tracking Options',
        canActivate: [permissionGuard('EditPostings')],
        loadComponent: () => import('./pages/dashboard/tracking-options/tracking-options.component').then((m) => m.TrackingOptionsComponent),
      },
      {
        path: 'style-guide',
        title: 'Style Guide',
        loadComponent: () => import('./pages/dashboard/style-guide/style-guide.component').then((m) => m.StyleGuideComponent),
      },
      {
        path: 'my-account',
        title: 'My Account',
        loadComponent: () => import('./pages/dashboard/my-account/my-account.component').then((m) => m.MyAccountComponent),
      },
      {
        path: 'users',
        title: 'Users',
        canActivate: [statewideGuard, permissionGuard('ManageUsers')],
        loadComponent: () => import('./pages/dashboard/users/users.component').then((m) => m.StatewideUsersComponent),
      },
      {
        path: 'users/new',
        title: 'New User',
        canActivate: [statewideGuard, permissionGuard('ManageUsers')],
        loadComponent: () => import('./pages/dashboard/user-create/user-create.component').then((m) => m.UserCreateComponent),
      },
      {
        path: 'system-users',
        title: 'System Users',
        canActivate: [permissionGuard('ManageUsers')],
        loadComponent: () => import('./pages/dashboard/system-users/system-users.component').then((m) => m.SystemUsersComponent),
      },
      {
        path: 'system-users/:id',
        title: 'Edit System User',
        canActivate: [permissionGuard('ManageUsers')],
        loadComponent: () => import('./pages/dashboard/system-user-edit/system-user-edit.component').then((m) => m.SystemUserEditComponent),
      },
      {
        path: 'roles',
        title: 'Roles',
        canActivate: [permissionGuard('ManageRoles')],
        loadComponent: () => import('./pages/dashboard/roles/roles.component').then((m) => m.RolesComponent),
      },
      {
        path: 'roles/new',
        title: 'New Role',
        canActivate: [permissionGuard('ManageRoles')],
        loadComponent: () => import('./pages/dashboard/role-edit/role-edit.component').then((m) => m.RoleEditComponent),
      },
      {
        path: 'roles/:id',
        title: 'Edit Role',
        canActivate: [permissionGuard('ManageRoles')],
        loadComponent: () => import('./pages/dashboard/role-edit/role-edit.component').then((m) => m.RoleEditComponent),
      },
      {
        path: 'customers',
        title: 'Agencies',
        canActivate: [statewideGuard, permissionGuard('ManageCustomers')],
        loadComponent: () => import('./pages/dashboard/customers/customers.component').then((m) => m.CustomersComponent),
      },
      {
        path: 'signup-requests',
        title: 'Signup Requests',
        canActivate: [statewideGuard, permissionGuard('ManageSignupRequests')],
        loadComponent: () => import('./pages/dashboard/signup-requests/signup-requests.component').then((m) => m.SignupRequestsComponent),
      },
      {
        path: 'customers/:id',
        title: 'Agency Profile',
        canActivate: [anyPermissionGuard(['ManageCustomers', 'EditOwnCustomer'])],
        loadComponent: () => import('./pages/dashboard/customer-edit/customer-edit.component').then((m) => m.CustomerEditComponent),
      },
      {
        path: 'my-customer',
        title: 'My Agency',
        canActivate: [permissionGuard('EditOwnCustomer')],
        loadComponent: () => import('./pages/dashboard/my-customer-redirect/my-customer-redirect.component').then((m) => m.MyCustomerRedirectComponent),
      },
      {
        path: 'rate-catalog',
        title: 'Rate Catalog',
        canActivate: [statewideGuard, permissionGuard('ManageRateCatalog')],
        loadComponent: () => import('./pages/dashboard/rate-catalog/rate-catalog.component').then((m) => m.RateCatalogComponent),
      },
      {
        path: 'rate-catalog/:id',
        title: 'Edit Product',
        canActivate: [statewideGuard, permissionGuard('ManageRateCatalog')],
        loadComponent: () => import('./pages/dashboard/rate-catalog-edit/rate-catalog-edit.component').then((m) => m.RateCatalogEditComponent),
      },
      {
        path: 'invoices',
        title: 'Invoices',
        canActivate: [permissionGuard('ViewBilling')],
        loadComponent: () => import('./pages/dashboard/invoices/invoices.component').then((m) => m.InvoicesComponent),
      },
      {
        path: 'splash-ads/buy',
        title: 'Buy Splash Ad',
        canActivate: [permissionGuard('ViewBilling')],
        loadComponent: () => import('./pages/dashboard/splash-ad-purchase/splash-ad-purchase.component').then((m) => m.SplashAdPurchaseComponent),
      },
      {
        path: 'splash-ads',
        title: 'Splash Ads',
        canActivate: [statewideGuard, permissionGuard('ManageBilling')],
        loadComponent: () => import('./pages/dashboard/splash-ads/splash-ads.component').then((m) => m.SplashAdsComponent),
      },
      {
        path: 'billing-automations',
        title: 'Billing Automations',
        canActivate: [statewideGuard, permissionGuard('ManageBilling')],
        loadComponent: () => import('./pages/dashboard/billing-automations/billing-automations.component').then((m) => m.BillingAutomationsComponent),
      },
      {
        path: 'invoices/:id',
        title: 'Invoice',
        canActivate: [permissionGuard('ViewBilling')],
        loadComponent: () => import('./pages/dashboard/invoice-edit/invoice-edit.component').then((m) => m.InvoiceEditComponent),
      },
      {
        path: 'service-agreement-templates',
        title: 'Service Agreement Templates',
        canActivate: [statewideGuard, permissionGuard('ManageServiceAgreements')],
        loadComponent: () => import('./pages/dashboard/service-agreement-templates/service-agreement-templates.component').then((m) => m.ServiceAgreementTemplatesComponent),
      },
      {
        path: 'service-agreement-templates/:id',
        title: 'Edit Service Agreement Template',
        canActivate: [statewideGuard, permissionGuard('ManageServiceAgreements')],
        loadComponent: () => import('./pages/dashboard/service-agreement-template-edit/service-agreement-template-edit.component').then((m) => m.ServiceAgreementTemplateEditComponent),
      },
      {
        path: 'system-emails',
        title: 'System Emails',
        canActivate: [statewideGuard, permissionGuard('ManageSystemEmails')],
        loadComponent: () => import('./pages/dashboard/system-emails/system-emails.component').then((m) => m.SystemEmailsComponent),
      },
      {
        path: 'system-emails/:id',
        title: 'Edit System Email',
        canActivate: [statewideGuard, permissionGuard('ManageSystemEmails')],
        loadComponent: () => import('./pages/dashboard/system-email-edit/system-email-edit.component').then((m) => m.SystemEmailEditComponent),
      },
      {
        path: 'email-test-redirect',
        title: 'Test Email Redirect',
        canActivate: [statewideGuard, permissionGuard('ManageSystemEmails')],
        loadComponent: () => import('./pages/dashboard/email-test-redirect/email-test-redirect.component').then((m) => m.EmailTestRedirectComponent),
      },
      {
        path: 'system-notice',
        title: 'Site Notice',
        canActivate: [statewideGuard, permissionGuard('ManageSystemEmails')],
        loadComponent: () => import('./pages/dashboard/system-notice/system-notice.component').then((m) => m.SystemNoticeComponent),
      },
      {
        path: 'faqs',
        title: 'Help Center FAQs',
        canActivate: [statewideGuard, permissionGuard('ManageSystemEmails')],
        loadComponent: () => import('./pages/dashboard/faqs/faqs.component').then((m) => m.FaqsComponent),
      },
      {
        path: 'email-log',
        title: 'Email Log',
        canActivate: [statewideGuard, permissionGuard('ManageSystemEmails')],
        loadComponent: () => import('./pages/dashboard/email-log/email-log.component').then((m) => m.EmailLogComponent),
      },
      {
        path: 'service-agreements/current',
        title: 'Service Agreement',
        // Billing contacts get identity-based access — they must reach the agreement to sign it.
        canActivate: [billingContactOrPermissionGuard('ViewBilling')],
        loadComponent: () => import('./pages/dashboard/service-agreement-current-redirect/service-agreement-current-redirect.component').then((m) => m.ServiceAgreementCurrentRedirectComponent),
      },
      {
        path: 'service-agreements/:id',
        title: 'Service Agreement',
        canActivate: [billingContactOrPermissionGuard('ViewBilling')],
        loadComponent: () => import('./pages/dashboard/service-agreement/service-agreement.component').then((m) => m.ServiceAgreementComponent),
      },
      {
        path: 'customers/:customerId/credits',
        title: 'Credit Ledger',
        canActivate: [permissionGuard('ViewBilling')],
        loadComponent: () => import('./pages/dashboard/credits/credits.component').then((m) => m.CreditsComponent),
      },
      {
        path: 'my-credits',
        title: 'My Credits',
        canActivate: [permissionGuard('ViewBilling')],
        loadComponent: () => import('./pages/dashboard/my-credits-redirect/my-credits-redirect.component').then((m) => m.MyCreditsRedirectComponent),
      },
    ],
  },
  { path: '**', redirectTo: '/login' },
];
